import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getUserPoints,
  createPoint as createPointApi,
  deletePoint as deletePointApi,
  updatePoint as updatePointApi,
} from '../api/points';
import { Point } from '../types/point';

interface PointsState {
  items: Point[];
  loading: boolean;
  error: string | null;
}

const initialState: PointsState = {
  items: [],
  loading: false,
  error: null,
};

export const fetchPoints = createAsyncThunk('points/fetch', async (_, { rejectWithValue }) => {
  try {
    const response = await getUserPoints();
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Не удалось загрузить пиксели');
  }
});

export const createPoint = createAsyncThunk('points/create', async (name: string, { rejectWithValue }) => {
  try {
    const response = await createPointApi(name);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Не удалось создать пиксель');
  }
});

export const deletePoint = createAsyncThunk('points/delete', async (id: number, { rejectWithValue }) => {
  try {
    await deletePointApi(id);
    return id;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Не удалось удалить пиксель');
  }
});

export const updatePoint = createAsyncThunk(
  'points/update',
  async ({ id, name, color }: { id: number; name: string; color?: string }, { rejectWithValue }) => {
    try {
      const response = await updatePointApi(id, name, color);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || 'Не удалось обновить пиксель');
    }
  },
);

const pointsSlice = createSlice({
  name: 'points',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPoints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPoints.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchPoints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load points';
      })
      .addCase(createPoint.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(createPoint.rejected, (state, action) => {
        state.error = (action.payload as string) ?? null;
      })
      .addCase(deletePoint.fulfilled, (state, action) => {
        state.items = state.items.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePoint.rejected, (state, action) => {
        state.error = (action.payload as string) ?? null;
      })
      .addCase(updatePoint.fulfilled, (state, action) => {
        const index = state.items.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      })
      .addCase(updatePoint.rejected, (state, action) => {
        state.error = (action.payload as string) ?? null;
      });
  },
});

export default pointsSlice.reducer;